import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { IconScan } from './icons/AnalyzerIcons';
import { postScanImage, type ScanImageSuccess } from '../api/endpoints';
import { getErrorMessage } from '../utils/errors';

export type ImageScannerResult = {
  barcode: string;
  product_id: number;
  brand?: string;
  name?: string;
  image_url?: string;
};

type ImageScannerButtonProps = {
  onResult: (result: ImageScannerResult) => void;
  onError: (error: string) => void;
  disabled?: boolean;
  isPlusOrPremium: boolean;
  className?: string;
  variant?: 'button' | 'icon';
  buttonLabel?: string;
  showCameraAction?: boolean;
};

const EASE = [0.22, 0.61, 0.36, 1] as [number, number, number, number];

export default function ImageScannerButton({
  onResult,
  onError,
  disabled = false,
  isPlusOrPremium,
  className = '',
  variant = 'button',
  buttonLabel = 'Upload image',
  showCameraAction = true,
}: ImageScannerButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (file: File) => {
    if (!isPlusOrPremium) {
      onError('Image scanning is available only for Plus and Premium subscribers. Upgrade your plan to unlock this feature!');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      onError('Image too large (max 2MB)');
      return;
    }
    setUploading(true);
    try {
      const result: ScanImageSuccess = await postScanImage(file);
      // barcode is optional: slug-identified products (e.g. local brands
      // without an EAN in open databases) are created without a barcode —
      // product_id is the canonical identifier.
      if (result.ok && result.product_id) {
        onResult({
          barcode: result.barcode,
          product_id: result.product_id,
          brand: result.brand,
          name: result.name,
          image_url: result.image_url,
        });
      } else if (!result.ok) {
        onError(result.message ?? 'Product not recognized or not a skincare/cosmetic product');
      } else {
        onError('Product not recognized or not a skincare/cosmetic product');
      }
    } catch (error_) {
      onError(getErrorMessage(error_, 'Failed to scan image'));
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFileSelect(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClick = () => {
    if (disabled || uploading) return;
    fileInputRef.current?.click();
  };

  const handleCameraClick = () => {
    if (!isPlusOrPremium) {
      onError('Image scanning is available only for Plus and Premium subscribers. Upgrade your plan to unlock this feature!');
      return;
    }
    if (disabled || uploading) return;

    const cameraInput = document.createElement('input');
    cameraInput.type = 'file';
    cameraInput.accept = 'image/*';
    cameraInput.capture = 'environment';
    cameraInput.style.display = 'none';
    document.body.appendChild(cameraInput);

    cameraInput.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) void handleFileSelect(file);
      document.body.removeChild(cameraInput);
    };
    cameraInput.oncancel = () => document.body.removeChild(cameraInput);
    cameraInput.click();
  };

  if (variant === 'icon') {
    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled || uploading}
        />
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled || uploading}
          className={`relative transition-all duration-[180ms] ${!isPlusOrPremium ? 'opacity-60 hover:opacity-90' : 'hover:scale-105'} ${className}`}
          style={{ color: 'var(--c-kivo-teal)' }}
          title={isPlusOrPremium ? 'Upload or scan product image' : 'Plus/Premium feature — upgrade to scan'}
        >
          {uploading ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-5 h-5 rounded-full border-2 border-t-transparent"
              style={{ borderColor: 'var(--c-kivo-teal)', borderTopColor: 'transparent' }}
            />
          ) : (
            <IconScan />
          )}
          {!isPlusOrPremium && (
            <span
              className="absolute -top-1 -right-1 w-2 h-2 rounded-full border border-white"
              style={{ background: 'var(--c-coral)' }}
            />
          )}
        </button>
      </>
    );
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled || uploading}
      />
      <div className="flex flex-col gap-2.5">
        <motion.button
          type="button"
          onClick={handleClick}
          disabled={disabled || uploading}
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.18, ease: EASE }}
          className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full border transition-colors duration-[180ms] disabled:opacity-50 ${className}`}
          style={isPlusOrPremium ? {
            background: 'var(--c-cream)',
            color: 'var(--c-charcoal)',
            borderColor: 'var(--c-charcoal-12)',
          } : {
            background: 'var(--c-cream)',
            color: 'var(--c-charcoal)',
            borderColor: 'rgba(244,168,142,0.7)',
            borderWidth: '1.5px',
          }}
        >
          {uploading ? (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-4 h-4 rounded-full border-2 border-t-transparent flex-shrink-0"
                style={{ borderColor: 'var(--c-kivo-teal)', borderTopColor: 'transparent' }}
              />
              <span>Scanning…</span>
            </>
          ) : (
            <>
              <IconScan />
              <span>{buttonLabel}</span>
              {!isPlusOrPremium && (
                <span
                  className="text-[9px] font-semibold px-2 py-0.5 rounded-full"
                  style={{
                    background: 'rgba(244,168,142,0.22)',
                    color: 'var(--c-error)',
                    letterSpacing: '0.08em',
                  }}
                >
                  PLUS
                </span>
              )}
            </>
          )}
        </motion.button>

        {isPlusOrPremium && showCameraAction && (
          <motion.button
            type="button"
            onClick={handleCameraClick}
            disabled={disabled || uploading}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.18, ease: EASE }}
            className="btn btn--ghost inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full disabled:opacity-50"
          >
            <IconScan />
            <span>Take photo</span>
          </motion.button>
        )}
      </div>
    </>
  );
}
